// src/components/ScheduledJobs.tsx

import React, { useEffect, useState } from "react";
import { fetchSummary } from "../api/fetchData";
import type { SummaryCall } from "../types";
import { useAuth } from '../hooks/useAuth';

interface ScheduledJob {
  id: string;
  type: string;
  // prompt 字段依然可用，但不再渲染
  prompt?: string;
  scheduledAt: string;
}

export default function ScheduledJobs() {
  const [jobs, setJobs] = useState<ScheduledJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<Record<string, boolean>>({});
  const { user, logout } = useAuth();
  useEffect(() => {
    fetchSummary(user.token)
      .then(({ calls }) => {
        // 只保留 queued 状态的任务
        const queued = calls
          .filter((c) => c.status === "queued")
          .map<ScheduledJob>((c) => ({
            id: c.job_id,
            type: c.metadata.type,
            prompt: (c.metadata as any).prompt,
            scheduledAt: c.received_at,
          }));
        setJobs(queued);
        setError(null);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load scheduled jobs.");
        setJobs([]);
      });
  }, []);

  const handleCancel = (jobId: string) => {
    setCancelling((c) => ({ ...c, [jobId]: true }));
    fetch("https://demo.aiguardian.io/agent-backend/api/canceljob", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // 如果后端需要 JWT 鉴权，请替换下面的 token
        Authorization:
          "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ3YW5nIn0.Cq6iOPhSv43zOFdqH_c7uKpZ53-AMOYljRXE4yqHgII",
      },
      body: JSON.stringify({ job_id: jobId }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("取消失败");
        // 从列表里移除已取消的任务
        setJobs((prev) => prev?.filter((j) => j.id !== jobId) ?? null);
        // 取消成功提示
        alert(`Job ${jobId} has been canceled successfully.`);
      })
      .catch(() => {
        alert("Failed to cancel job " + jobId);
      })
      .finally(() => {
        setCancelling((c) => ({ ...c, [jobId]: false }));
      });
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow-md space-y-2">
      <h3 className="text-sm font-medium text-gray-500">Scheduled Jobs</h3>

      {error && (
        <div className="bg-red-100 text-red-700 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {!error && jobs !== null && jobs.length === 0 && (
        <div className="text-gray-400 text-sm">No scheduled jobs.</div>
      )}

      {jobs && jobs.length > 0 && (
        <ul className="divide-y divide-gray-200">
          {jobs.map((job) => (
            <li key={job.id} className="flex justify-between items-start py-2">
              <div className="space-y-0.5">
                {/* 只显示类型和 ID */}
                <div className="font-medium text-gray-800">{job.type}</div>
                <div className="text-xs text-gray-400">#{job.id}</div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-xs text-gray-500">
                  {new Date(job.scheduledAt).toLocaleString()}
                </div>
                <button
                  onClick={() => handleCancel(job.id)}
                  disabled={!!cancelling[job.id]}
                  className={`
                    text-xs font-medium
                    ${
                      cancelling[job.id]
                        ? "text-gray-400"
                        : "text-red-600 hover:underline"
                    }
                  `}
                >
                  {cancelling[job.id] ? "Cancelling…" : "Cancel"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
